import { useBrainStore } from '@/state/brainStore';
import { useSimulationStore } from '@/state/simulationStore';
import { generateDepositLink, recordManualPayment } from '@/domain/depositFlow';
import { recallOverlappingHolds } from '@/domain/resendRoutingFlow';
import { onBookingConfirmed } from '@/domain/bookingConfirmationFlow';
import { depositForDuration, sessionLengthForDuration } from '@/domain/pricing';
import { eventBus } from '@/events/eventBus';
import type { Booking } from '@/domain/types';

/**
 * How the deposit is taken when a Sweeper gap is filled (board 08, the client's
 * choice on the call):
 *  - 'phone-eftpos': card over the phone now — the slot locks instantly.
 *  - 'dated-link': a dated + artist deposit link with a firm 24h hold.
 */
export type GapFillDepositMethod = 'phone-eftpos' | 'dated-link';

export interface GapFillInput {
  customerId: string;
  artistId: string;
  startIso: string;
  endIso: string;
  method: GapFillDepositMethod;
  /** Free-text piece/brief captured on the call, if any. */
  designBrief?: string;
  /** Channel the dated link is sent on (dated-link only). Defaults to SMS. */
  channel?: 'sms' | 'meta-dm' | 'ig-dm' | 'email';
}

export interface GapFillResult {
  bookingId?: string;
  depositId?: string;
}

function nowIso(): string {
  return useSimulationStore.getState().clockIso;
}

function slotMinutes(startIso: string, endIso: string): number {
  return Math.max(15, Math.round((new Date(endIso).getTime() - new Date(startIso).getTime()) / 60000));
}

/**
 * Fill a Sweeper gap, confirmed on the call (bd tv-pt8, board 08 + spine 01).
 *
 * A Sweeper gap-fill is ALWAYS verbally confirmed on the spot and bypasses the
 * pre-appointment confirmations cadence: the booking carries `confirmedOnCall`,
 * so `confirmationWorklist` never surfaces it as a call/text lane. The
 * confirmation message + waiver still go out via the shared confirm hook the
 * moment the booking is confirmed. Confirm-on-call is a fixed property of the
 * gap-fill, not an operator toggle.
 *
 * The operator's one choice is how the deposit is taken:
 *  - 'phone-eftpos': card over the phone now. The slot locks instantly — a paid
 *    EFTPOS deposit against a confirmed booking, waiver + calendar invite out now.
 *  - 'dated-link': a dated + artist deposit link with a firm 24h hold. The slot
 *    is held; paid within 24h it confirms, otherwise it lapses onto the Sweeper's
 *    Missed-deposits list (no 2h overbook).
 *
 * "Now" is the sim clock. No-op (empty result) when the customer is unknown.
 */
export function confirmGapFillOnCall(input: GapFillInput): GapFillResult {
  const brain = useBrainStore.getState();
  const customer = brain.customers[input.customerId];
  if (!customer) return {};

  const at = nowIso();
  const durationMin = slotMinutes(input.startIso, input.endIso);
  const sessionLength = sessionLengthForDuration(durationMin);
  const amountAud = depositForDuration(durationMin);
  const studioId = customer.primaryStudioId;

  if (input.method === 'dated-link') {
    // Firm 24h hold: generateDepositLink mints the held ghost booking + a pending
    // deposit (24h expiry) against the exact slot. Flag the hold confirmed-on-call
    // so it skips the cadence even before the deposit lands; the confirm message +
    // waiver fire on payment (the existing paid-webhook confirm path).
    const deposit = generateDepositLink({
      customerId: input.customerId,
      studioId,
      artistId: input.artistId,
      amountAud,
      channel: input.channel ?? 'sms',
      sessionLength,
      source: 'agent-initiated-chat',
      discussedSlot: { artistId: input.artistId, startIso: input.startIso, endIso: input.endIso },
      // Sweeper gap-fill links are a firm 24h lock with no 2h overbook, even inside
      // this week (tv-nid) - unlike agent dated-links, which are overbookable.
      firmHold: true,
      ...(input.designBrief ? { pieceName: input.designBrief } : {}),
    });
    if (deposit.heldBookingId) {
      const held = useBrainStore.getState().bookings[deposit.heldBookingId];
      if (held) brain.upsertBooking({ ...held, confirmedOnCall: true });
    }
    eventBus.publish('gapfill:confirmed-on-call', {
      bookingId: deposit.heldBookingId,
      depositId: deposit.id,
      method: input.method,
      at,
    });
    return { bookingId: deposit.heldBookingId, depositId: deposit.id };
  }

  // 'phone-eftpos': lock instantly. Create the confirmed booking on the slot,
  // take the EFTPOS deposit against it, clear any chase sequence, then fire the
  // shared confirm hook (waiver + calendar invite).
  const bookingId = `bk-gapfill-${input.customerId}-${new Date(input.startIso).getTime()}`;
  const booking: Booking = {
    id: bookingId,
    customerId: input.customerId,
    studioId,
    artistId: input.artistId,
    start: input.startIso,
    durationMin,
    sessionLength,
    status: 'confirmed',
    confirmedAt: at,
    confirmedOnCall: true,
    designBrief: input.designBrief ?? 'Gap filled on the call.',
    referencePhotos: [],
    source: 'agent-initiated-chat',
    createdAt: at,
  };
  brain.upsertBooking(booking);
  recordManualPayment({ bookingId, amountAud, method: 'eftpos' });
  brain.clearSequenceState(input.customerId);
  onBookingConfirmed(bookingId);
  // Slot locked on the call: void + re-call any other overbookable hold on it.
  recallOverlappingHolds(bookingId);

  const depositId = useBrainStore.getState().bookings[bookingId]?.depositId;
  eventBus.publish('gapfill:confirmed-on-call', { bookingId, depositId, method: input.method, at });
  return { bookingId, depositId };
}
